import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';

import { MetricsNames } from '@/comp/MapBuilder/MetricsNames';
import { ControlIcon } from '@/comp/MapBuilder/components/ControlIcon/ControlIcon';
import { MaintenanceSwitch } from '@/comp/StationControlCenter/ToggleSwitches/MaintenanceSwitch';
import { PowerOnOffSwitch } from '@/comp/StationControlCenter/ToggleSwitches/PowerOnOffSwitch';
import { getPoweredOnState } from '@/comp/StationControlCenter/SccUtils';
import { Ports } from '@/comp/MapBuilder/components/Ports';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useStores } from '@/hooks/use-stores';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import { isSelectedNodeOnMap, isValueHighlighted } from '@/comp/MapBuilder/utils/utils';
import GetConnectivityIconAndStatus from '@/comp/MapBuilder/nodes/compressor/GetConnectivityIconAndStatus/GetConnectivityIconAndStatus';

import { getPowerSensorKw, getPowerValue, getFirstPowerSensor } from '../compressor/CompressorNodeWidget.utils';
import { BlowerBackground } from './BlowerBackground';
import { Container } from './styled';

export const BlowerNodeWidget = observer(({ engine = null, node, entity, width, height, renderedOnMap = null }) => {
  const { t } = useTranslation();
  const { stationStore, mapBuilderStore } = useStores();
  const { tvalue } = useMeasurement();
  const { tcvalue, tcunit } = useValueByConnectivity();
  const {
    hp,
    brand,
    modelDisplayName,
    name,
    connectivity,
    activity,
    extendedActivity,
    inMaintenance,
    poweredOn,
    messages,
    controlMode,
    deviceId,
  } = entity;
  const onDevices = stationStore.currentStation?.devices?.filter(device => entity.onDevices.includes(device.deviceId));
  const firstPowerSensor = getFirstPowerSensor(entity, onDevices);
  const firstPowerSensorKw = getPowerSensorKw(firstPowerSensor);
  const poweredOnState = getPoweredOnState(poweredOn);

  const isSelected = isSelectedNodeOnMap(entity.deviceId, mapBuilderStore.selectedDevice?.deviceId, renderedOnMap);

  const connectivityIconAndStatus = GetConnectivityIconAndStatus({
    connectivity,
    activity,
    extendedActivity,
    controlMode,
    isSelectedOnMap: isSelected,
    size: engine ? '25px' : '42px',
  });

  const mapViewMetrics = [
    {
      name: MetricsNames.HorsePower,
      value: tvalue(hp, 'HP'),
      unit: 'HP',
      highlight: isValueHighlighted(MetricsNames.HorsePower),
    },
    {
      name: MetricsNames.Power,
      value: getPowerValue(entity, stationStore.currentStation?.id, firstPowerSensorKw, tvalue),
      unit: 'kw',
    },
  ];

  function getMetricElement(metric) {
    return (
      <>
        <span className={`metrics-list-item-value ${isValueHighlighted(metric.name) && 'highlighted'}`}>
          {tcvalue(metric.value, connectivity)}
        </span>
        <span className="metrics-list-item-label">{tcunit(t(metric.unit), metric.value, connectivity)}</span>
      </>
    );
  }

  return (
    <Container
      width={width}
      height={height}
      $mapView={engine ? -1 : 0}
      className="compressor-node-widget"
      $isSelected={isSelected}
      $isInMaintenance={inMaintenance}
      $poweredOnState={poweredOnState}
    >
      <BlowerBackground isSelected={isSelected} mapView={engine} />

      {!!engine && (
        <div className="status-widget-wrapper">
          <header>
            <div className="title">{!poweredOnState ? t('STOPPED') : t('In maintenance')}</div>
          </header>
          <main />
        </div>
      )}

      <div className="toggles-wrapper">
        {poweredOn != null && (
          <div className="toggle-status-box">
            <PowerOnOffSwitch deviceId={deviceId} forceDisable={true} />
          </div>
        )}
        {!isNil(inMaintenance) && poweredOn != null && <div className="lower-right-border" />}
        {!isNil(inMaintenance) && (
          <div className="toggle-status-box">
            <MaintenanceSwitch inMaintenance={inMaintenance} deviceId={deviceId} />
          </div>
        )}
      </div>
      <div className="top-row">
        <div className="control-title-wrapper">
          <ControlIcon
            messages={messages}
            controlMode={controlMode}
            connectivity={connectivity}
            className={'control-icon'}
            width={engine ? '29px' : '48px'}
            height={engine ? '29px' : '48px'}
          />
          {!!engine && (
            <div className="min0 title">
              <div className="name ellipsis">{name}</div>
              <div className="model ellipsis">
                {modelDisplayName} {brand}
              </div>
            </div>
          )}
        </div>
        <div className={'connectivity-icon-wrapper'}>{connectivityIconAndStatus.icon}</div>
      </div>

      {!!engine && (
        <>
          <section>
            <div className="metrics-list">
              {mapViewMetrics.map(
                metric =>
                  (metric.name === MetricsNames.Power || metric.value != null) && (
                    <div className="metrics-list-item" key={metric.name}>
                      {getMetricElement(metric)}
                    </div>
                  ),
              )}
            </div>

            <AlertsWidget alerts={node?.alerts} />
          </section>

          <Ports node={node} engine={engine} width={width} height={height} />
        </>
      )}
      <div className="upper-right-border" />
    </Container>
  );
});
